import { Column } from '@access-erp/common';
import {
  CellClickedEvent,
  CellType,
  FilterValueTypes,
  ICellRendererParams,
  Tooltip,
} from '@access-erp/ui-components';

import { MutableRefObject } from 'react';

import { renderBillingType } from '@/components/common/EnquiriesScreen/customFilters/BillingTypeMultiSelect';
import {
  createLinkCellRenderer,
  shouldSkipCellClickNavigation
} from '@/components/common/EnquiriesScreen/components/LinkCellRenderer';
import { filterPurchaseOrdersStatuses, filterStatuses, contractLinePauseStatuses, filterContractOrigins }
  from '@/components/common/EnquiriesScreen/utils/statuses.config';
import { EnquiriesPill } from '@/components/common/EnquiriesScreen/components/EnquiriesPill';
import { EnvUtils } from '@/utils/env.utils';
import { filtersforBillingTypes, filtersforStringifiedNumbers }
  from '@/components/common/EnquiriesScreen/utils/filtersForStringigfiedNumbers';
import { CellFormatters } from '@/components/common/EnquiriesScreen/utils/CellFormatters';
import { guidFilterOptions } from '@/components/common/EnquiriesScreen/utils/filters.config';
import {
  ContractBillingStatusPascalCaseEnum,
  ContractLineBillingCycleOutput
} from '@/graphql/contracts/_generated_/contracts';
import { NOT_AVAILABLE_CHAR } from '@/config/common.config';
import { lastNextDayGap, dateCellRenderer } from '@/pages/ContractDetails/ContractLines/grid/helper';

export interface UseTransactionTableColumnsReturn {
  headerColumns: Column<unknown>[];
}

type ReasonCode = {
  code: string;
  name: string;
};

type DefaultColumnArgsType = {
  getContractLink: (contractId: string, searchString?: string) => string;
  goToContract: (contractLink: string) => void;
  reasonCodesRef?: MutableRefObject<ReasonCode[]>;
};

const VISIBLE_REASON_CHARS_LENGTH = 50;

export const defaultColumns = ({
  getContractLink,
  goToContract,
  reasonCodesRef,
}: DefaultColumnArgsType): Column<any>[] => {
  let filterIndex = 1;
  const nextFilter = () => filterIndex++;

  return [
    {
      name: 'contractNumber',
      caption: 'Contract Number',
      width: 200,
      cellType: CellType.String,
      defaultFilter: nextFilter(),
      cellRenderer: createLinkCellRenderer(({ data, searchString }) => {
        const contractId = data?.contractId ?? '';
        if (!contractId) {
          return '#';
        }
        return getContractLink(contractId, searchString);
      }),
      onCellClicked: (e: CellClickedEvent) => {
        if (shouldSkipCellClickNavigation(e)) {
          return;
        }
        const searchStringIfAny = e.context?.serverSide?.current?.search;
        const contractId = e.data.contractId ?? '';
        goToContract(getContractLink(contractId, searchStringIfAny));
      }
    },
    {
      name: 'customerCode',
      caption: 'Contracted Account',
      width: 200,
      cellType: CellType.String,
      defaultFilter: nextFilter(),
    },
    {
      name: 'customerName',
      caption: 'Contracted Name',
      width: 200,
      cellType: CellType.String,
      defaultFilter: nextFilter(),
    },
    {
      name: 'customerAddress',
      caption: 'Contracted Address',
      width: 200,
      cellType: CellType.String,
      defaultFilter: nextFilter(),
    },
    {
      name: 'customerPostcode',
      caption: 'Contracted Postcode',
      width: 200,
      cellType: CellType.String,
      defaultFilter: nextFilter(),
    },
    ...(!EnvUtils.isProRataFirstPeriodEnabled()
      ? [
        {
          name: 'lineStartDate',
          caption: 'Line Start Date',
          width: 180,
          cellType: CellType.Date,
          cellFormatter: CellFormatters.date,
          defaultFilter: nextFilter(),
        },
      ]
      : []),
    {
      name: 'billingInterval',
      caption: 'Billing Interval',
      width: 160,
      cellType: CellType.String,
      defaultFilter: nextFilter(),
    },
    {
      name: 'minCharge',
      caption: 'Min Charge',
      width: 110,
      cellType: CellType.String,
      cellFormatter: (params) => CellFormatters.decimal(params),
      defaultFilter: nextFilter(),
      cellStyle: { justifyContent: 'end' },
    },
    {
      name: 'salesHandoverNumber',
      caption: 'Sales Handover Number',
      width: 180,
      cellType: CellType.String,
      defaultFilter: nextFilter(),
    },
    {
      name: 'contractStatus',
      caption: 'Contract Status',
      width: 170,
      cellType: CellType.String,
      filterType: FilterValueTypes.EnumFilter,
      filterData: filterStatuses,
      defaultFilter: nextFilter(),
    },
    {
      name: 'contractLineStatus',
      caption: 'Contract Line Status',
      width: 180,
      cellType: CellType.String,
      filterType: FilterValueTypes.EnumFilter,
      filterData: filterStatuses,
      defaultFilter: nextFilter(),
    },
    {
      name: 'stockCode',
      caption: 'Stock Code',
      width: 200,
      cellType: CellType.String,
      defaultFilter: nextFilter(),
    },
    {
      name: 'quantity',
      caption: 'Quantity',
      width: 160,
      cellType: CellType.Decimal,
      cellFormatter: CellFormatters.integer,
      defaultFilter: nextFilter(),
    },
    {
      name: 'unitType',
      caption: 'Unit Type',
      width: 100,
      cellType: CellType.String,
      defaultFilter: nextFilter(),
    },
    {
      name: 'unitPrice',
      caption: 'Unit Price',
      width: 170,
      cellType: CellType.String,
      cellFormatter: CellFormatters.decimal,
      defaultFilter: nextFilter(),
      cellStyle: { justifyContent: 'end' },
    },
    {
      name: 'unitPriceFrequency',
      caption: 'Unit Price Frequency',
      width: 160,
      cellType: CellType.String,
      valuePath: 'unitPriceFrequency',
      defaultFilter: nextFilter(),
    },
    {
      name: 'billingType',
      caption: 'Billing Type',
      width: 280,
      cellType: CellType.String,
      filterType: FilterValueTypes.StringMultiSelectFilter,
      renderFilter: (renderProps) => renderBillingType({ ...renderProps, title: 'Billing Type' }),
      filterOptions: {
        availableOptions: filtersforBillingTypes,
      },
      cellRenderer: (params: ICellRendererParams) => {
        const billingTypesArray = params.data?.billingType?.split?.(',');
        return <EnquiriesPill value={billingTypesArray ?? ''} />;
      },
      defaultFilter: nextFilter(),
    },
    {
      name: 'nextInvoiceDate',
      caption: 'Next Invoice Date',
      width: 180,
      cellType: CellType.Date,
      cellFormatter: CellFormatters.date,
      groupable: false,
      disableRowGroup: true,
      sortable: false,
      filterable: false,
      suppressAdvancedFilter: true,
      defaultFilter: nextFilter(),
    },
    {
      name: 'comments',
      caption: 'Comments',
      width: 200,
      cellType: CellType.String,
      defaultFilter: nextFilter(),
    },
    {
      name: 'statusChangeComment',
      caption: 'Status Change Comment',
      width: 200,
      cellType: CellType.String,
      defaultFilter: nextFilter(),
    },
    {
      name: 'billingPeriodValue',
      caption: 'Billing Period Value',
      cellType: CellType.String,
      cellFormatter: (params) => CellFormatters.decimal(params),
      width: 180,
      defaultFilter: nextFilter(),
      cellStyle: { justifyContent: 'end' },
    },
    ...[
      {
        name: 'shipToCustomerAccountNumber',
        caption: 'Ship to Account',
        width: 200,
        cellType: CellType.String,
        defaultFilter: nextFilter(),
      },
      {
        name: 'shipToCustomerName',
        caption: 'Ship to Name',
        width: 200,
        cellType: CellType.String,
        defaultFilter: nextFilter(),
      },
      {
        name: 'shipToCustomerAddress',
        caption: 'Ship to Address',
        width: 200,
        cellType: CellType.String,
        defaultFilter: nextFilter(),
      },
      {
        name: 'shipToCustomerPostcode',
        caption: 'Ship to Postcode',
        width: 200,
        cellType: CellType.String,
        defaultFilter: nextFilter(),
      },
      {
        name: 'invoiceToCustomerAccountNumber',
        caption: 'Invoice to Account',
        width: 200,
        cellType: CellType.String,
        defaultFilter: nextFilter(),
      },
      {
        name: 'invoiceToCustomerName',
        caption: 'Invoice to Name',
        width: 200,
        cellType: CellType.String,
        defaultFilter: nextFilter(),
      },
      {
        name: 'invoiceToCustomerAddress',
        caption: 'Invoice to Address',
        width: 200,
        cellType: CellType.String,
        defaultFilter: nextFilter(),
      },
      {
        name: 'invoiceToCustomerPostcode',
        caption: 'Invoice to Postcode',
        width: 200,
        cellType: CellType.String,
        defaultFilter: nextFilter(),
      },
    ] as Column<unknown>[],
    {
      name: 'lastSOQuantity',
      caption: 'Last SO Quantity',
      cellType: CellType.Integer,
      cellFormatter: CellFormatters.integer,
      width: 180,
      groupable: false,
      disableRowGroup: true,
      sortable: false,
      filterable: false,
      suppressAdvancedFilter: true,
      defaultFilter: nextFilter(),
    },
    {
      name: 'lastSOTotalValue',
      caption: 'Last SO Total Value',
      cellType: CellType.String,
      cellFormatter: (params) => CellFormatters.decimal(params),
      width: 180,
      groupable: false,
      disableRowGroup: true,
      sortable: false,
      filterable: false,
      suppressAdvancedFilter: true,
      defaultFilter: nextFilter(),
      cellStyle: { justifyContent: 'end' },
    },
    {
      name: 'lastSOStartDate',
      caption: 'Last SO Start Date',
      cellType: CellType.Date,
      cellFormatter: CellFormatters.date,
      minWidth: 180,
      groupable: false,
      disableRowGroup: true,
      sortable: false,
      filterable: false,
      suppressAdvancedFilter: true,
      defaultFilter: nextFilter(),
    },
    {
      name: 'lastSOEndDate',
      caption: 'Last SO End Date',
      cellType: CellType.Date,
      cellFormatter: CellFormatters.date,
      minWidth: 180,
      groupable: false,
      disableRowGroup: true,
      sortable: false,
      filterable: false,
      suppressAdvancedFilter: true,
      defaultFilter: nextFilter(),
    },
    {
      name: 'annualBillingPeriodValue',
      caption: 'Annual Billing Period Value',
      cellType: CellType.String,
      cellFormatter: (params) => CellFormatters.decimal(params),
      width: 200,
      defaultFilter: nextFilter(),
      cellStyle: { justifyContent: 'end' },
    },
    {
      name: 'predictedSOStartDate',
      caption: 'Predicted SO start date',
      width: 180,
      cellType: CellType.Date,
      cellFormatter: CellFormatters.date,
      groupable: false,
      disableRowGroup: true,
      sortable: false,
      filterable: false,
      suppressAdvancedFilter: true,
      defaultFilter: nextFilter(),
    },
    {
      name: 'predictedSOEndDate',
      caption: 'Predicted SO end date',
      width: 180,
      cellType: CellType.Date,
      cellFormatter: CellFormatters.date,
      groupable: false,
      disableRowGroup: true,
      sortable: false,
      filterable: false,
      suppressAdvancedFilter: true,
      defaultFilter: nextFilter(),
    },
    {
      name: 'predictedSOInvoiceDate',
      caption: 'Predicted SO invoice date',
      width: 200,
      cellType: CellType.Date,
      cellFormatter: CellFormatters.date,
      groupable: false,
      disableRowGroup: true,
      sortable: false,
      filterable: false,
      suppressAdvancedFilter: true,
      defaultFilter: nextFilter(),
    },
    {
      name: 'predictedAnnualValue',
      caption: 'Predicted Annual Value',
      width: 180,
      cellType: CellType.String,
      cellFormatter: (params) => CellFormatters.decimal(params),
      groupable: false,
      disableRowGroup: true,
      sortable: false,
      filterable: false,
      suppressAdvancedFilter: true,
      defaultFilter: nextFilter(),
      cellStyle: { justifyContent: 'end' },
    },
    {
      name: 'nextBillingStartDate',
      caption: 'Next Billing Start Date',
      width: 180,
      cellType: CellType.Date,
      cellFormatter: CellFormatters.date,
      cellRenderer: ({
        value,
        data,
      }: ICellRendererParams<ContractLineBillingCycleOutput, string>) => {

        let displayValue = '';

        if (!value && data?.nextBillingStartDate == null) {
          displayValue = NOT_AVAILABLE_CHAR;
        }

        let dayGap = lastNextDayGap(data);
        displayValue = dateCellRenderer({ value });

        return (<>
          {(dayGap > 1) &&
            <Tooltip tooltipFor={<span>{'\u26A0\uFE0F'}</span>}>
              There is a {dayGap}-day gap between the last and next billed periods.
            </Tooltip>}
          {displayValue}
        </>);
      },
      groupable: false,
      disableRowGroup: true,
      sortable: false,
      filterable: false,
      suppressAdvancedFilter: true,
      defaultFilter: nextFilter(),
    },
    {
      name: 'nextBillingEndDate',
      caption: 'Next Billing End Date',
      width: 180,
      cellType: CellType.Date,
      cellFormatter: CellFormatters.date,
      cellRenderer: ({
        value,
        data,
      }: ICellRendererParams<ContractLineBillingCycleOutput, string>) => {

        let displayValue = '';

        if (!value && data?.nextBillingEndDate == null) {
          return NOT_AVAILABLE_CHAR;
        }

        let dayGap = lastNextDayGap(data);
        displayValue = dateCellRenderer({ value });

        return (<>
          {(dayGap > 1) &&
            <Tooltip tooltipFor={<span>{'\u26A0\uFE0F'}</span>}>
              There is a {dayGap}-day gap between the last and next billed periods.
            </Tooltip>}
          {displayValue}
        </>);
      },
      groupable: false,
      disableRowGroup: true,
      sortable: false,
      filterable: false,
      suppressAdvancedFilter: true,
      defaultFilter: nextFilter(),
    },
    {
      name: 'lastBillingStartDate',
      caption: 'Last Billing Start Date',
      width: 180,
      cellType: CellType.Date,
      cellFormatter: CellFormatters.date,
      groupable: false,
      disableRowGroup: true,
      sortable: false,
      filterable: false,
      suppressAdvancedFilter: true,
      defaultFilter: nextFilter(),
    },
    {
      name: 'lastBillingEndDate',
      caption: 'Last Billing End Date',
      width: 180,
      cellType: CellType.Date,
      cellFormatter: CellFormatters.date,
      groupable: false,
      disableRowGroup: true,
      sortable: false,
      filterable: false,
      suppressAdvancedFilter: true,
      defaultFilter: nextFilter(),
    },
    {
      name: 'contractCurrency',
      caption: 'Contract Currency',
      width: 150,
      cellType: CellType.String,
      defaultFilter: nextFilter(),
    },
    ...(EnvUtils.isProRataFirstPeriodEnabled()
      ? [
        {
          name: 'proRataEndDate',
          caption: 'Pro Rata End Date',
          width: 180,
          cellType: CellType.Date,
          cellFormatter: CellFormatters.date,
          defaultFilter: nextFilter(),
        },
      ]
      : []),
    ...[
      {
        name: 'poNumber',
        caption: 'PO Number',
        width: 160,
        cellType: CellType.String,
        defaultFilter: nextFilter(),
      },
      {
        name: 'poTotalValue',
        caption: 'PO Total Value',
        width: 200,
        cellType: CellType.String,
        defaultFilter: nextFilter(),
        cellFormatter: CellFormatters.decimal,
        groupable: false,
        filterOptions: {
          availableOptions: filtersforStringifiedNumbers,
        },
        cellStyle: { justifyContent: 'end' },
      },
      {
        name: 'poRemainingValue',
        caption: 'PO Remaining Value',
        width: 200,
        cellType: CellType.String,
        defaultFilter: nextFilter(),
        cellFormatter: CellFormatters.decimal,
        groupable: false,
        filterOptions: {
          availableOptions: filtersforStringifiedNumbers,
        },
        cellStyle: { justifyContent: 'end' },
      },
      {
        name: 'poDate',
        caption: 'PO Date',
        width: 160,
        cellType: CellType.Date,
        cellFormatter: CellFormatters.date,
        defaultFilter: nextFilter(),
      },
      {
        name: 'poStartDate',
        caption: 'PO Start Date',
        width: 200,
        cellType: CellType.Date,
        cellFormatter: CellFormatters.date,
        defaultFilter: nextFilter(),
      },
      {
        name: 'poEndDate',
        caption: 'PO End Date',
        width: 200,
        cellType: CellType.Date,
        cellFormatter: CellFormatters.date,
        defaultFilter: nextFilter(),
      },
      {
        name: 'poStatus',
        caption: 'PO Status',
        width: 160,
        cellType: CellType.String,
        filterData: filterPurchaseOrdersStatuses,
        filterType: FilterValueTypes.EnumFilter,
        defaultFilter: nextFilter(),
        cellRenderer: ({ value }: { value: any }) =>
          !!value && value !== 'Active' ? (
            <span>{'\u26A0\uFE0F ' + value}</span>
          ) : (
            <span>{value}</span>
          ),
      },
      {
        name: 'poNote',
        caption: 'PO Note',
        width: 250,
        cellType: CellType.String,
        defaultFilter: nextFilter(),
        filterable: false,
        suppressAdvancedFilter: true,
        disableRowGroup: true,
        sortable: false,
        cellRenderer: ({ value }: { value: any }) => {
          const reasonForFailureTrimmed =
            value && value.length > VISIBLE_REASON_CHARS_LENGTH
              ? value.slice(0, VISIBLE_REASON_CHARS_LENGTH) + '...'
              : value || '';

          const titlePopUp =
            value && value.length > VISIBLE_REASON_CHARS_LENGTH ? value : undefined;

          return <div title={titlePopUp}>{reasonForFailureTrimmed}</div>;
        },
      },
      {
        name: 'poCurrency',
        caption: 'PO Currency',
        width: 120,
        cellType: CellType.String,
        defaultFilter: nextFilter(),
      },
      {
        name: 'oppHeaderId',
        caption: 'Opp Header Id',
        width: 180,
        cellType: CellType.String,
        defaultFilter: nextFilter(),
      },
      {
        name: 'oppLineId',
        caption: 'Opp Line Id',
        width: 180,
        cellType: CellType.String,
        defaultFilter: nextFilter(),
      },
      {
        name: 'bundleDescription',
        caption: 'Bundle Description',
        width: 200,
        cellType: CellType.String,
      },
    ] as Column<unknown>[],
    ...([
      {
        name: 'lineBillingStatus',
        caption: 'Pause Status',
        width: 150,
        cellType: CellType.String,
        defaultFilter: nextFilter(),
        filterType: FilterValueTypes.EnumFilter,
        filterData: contractLinePauseStatuses,
        cellRenderer: (params: ICellRendererParams) => {
          const status = params.data?.lineBillingStatus;
          if (status === ContractBillingStatusPascalCaseEnum.On) {
            return <span>Off</span>;
          }

          if (status === ContractBillingStatusPascalCaseEnum.Paused) {
            return <span>Paused</span>;
          }

          if (status === ContractBillingStatusPascalCaseEnum.FuturePause) {
            return <span>FuturePause</span>;
          }

          return <span>{status ?? ''}</span>;
        },
      },
      {
        name: 'lineBillingCurrentStatusEndDate',
        caption: 'Pause End Date',
        width: 180,
        cellType: CellType.Date,
        cellFormatter: CellFormatters.date,
        defaultFilter: nextFilter(),
      },
      ...(EnvUtils.isNextSoIssueDateEnabled() ? [{
        name: 'nextSoIssueDate',
        caption: 'Next SO Issue Date',
        width: 180,
        cellType: CellType.Date,
        cellFormatter: CellFormatters.date,
        defaultFilter: nextFilter(),
        filterable: false,
        suppressAdvancedFilter: true,
      }] : []),
      {
        name: 'lineBillingStatusUpdateComment',
        caption: 'Pause Comment',
        width: 200,
        cellType: CellType.String,
        defaultFilter: nextFilter(),
        filterable: false,
        suppressAdvancedFilter: true,
        cellRenderer: ({ value }: { value: any }) => {
          const commentTrimmed = value && value.length > 50 ? value.slice(0, 50) + '...' : value || '';
          const titlePopUp = value && value.length > 50 ? value : undefined;
          return <div title={titlePopUp}>{commentTrimmed}</div>;
        },
      },
      {
        name: 'lineBillingStatusUpdateReference',
        caption: 'Pause Reference',
        width: 180,
        cellType: CellType.String,
        defaultFilter: nextFilter(),
      },
      {
        name: 'lineBillingStatusUpdateReasonCode',
        caption: 'Pause Reason',
        width: 200,
        cellType: CellType.String,
        defaultFilter: nextFilter(),
        cellRenderer: ({ value }: { value: any }) => {
          if (!value) return '';
          const reasonCode = reasonCodesRef?.current.find((rc) => rc.code === value);
          return reasonCode?.name ?? value;
        },
      },
      {
        name: 'lineBillingStatusUpdated',
        caption: 'Status Updated Date',
        width: 180,
        cellType: CellType.Date,
        cellFormatter: CellFormatters.date,
        defaultFilter: nextFilter(),
      },
    ] as Column<unknown>[]),
    {
      name: 'terminationDate',
      caption: 'Termination Date',
      width: 180,
      cellType: CellType.Date,
      cellFormatter: CellFormatters.date,
      defaultFilter: nextFilter(),
    },
    ...(EnvUtils.isContractLineMergeEnabled()
      ? [
        {
          name: 'isFromMerge',
          caption: 'Is From Merge',
          width: 140,
          cellType: CellType.Bool,
          defaultFilter: nextFilter(),
          cellRenderer: ({ value }: { value: boolean }) => value ? 'Yes' : 'No',
        },
        {
          name: 'mergedLineId',
          caption: 'Merged Line ID',
          width: 270,
          cellType: CellType.String,
          defaultFilter: nextFilter(),
          filterType: FilterValueTypes.StringFilter,
          filterOptions: {
            availableOptions: guidFilterOptions,
          },
        },
      ] as Column<unknown>[] : []),
    {
      name: 'origin',
      caption: 'Origin',
      width: 130,
      cellType: CellType.String,
      filterType: FilterValueTypes.EnumFilter,
      filterData: filterContractOrigins,
      defaultFilter: nextFilter(),
    },
    {
      name: 'id',
      caption: 'ID',
      width: 270,
      cellType: CellType.String,
      defaultFilter: nextFilter(),
      hidden: true,
      filterType: FilterValueTypes.StringFilter,
      filterOptions: {
        availableOptions: guidFilterOptions,
      }
    },
  ];
};

export const getHeaderColumns = (defaultColumnArgs: DefaultColumnArgsType): Column<any>[] =>
  defaultColumns(defaultColumnArgs);
